import React from 'react';
import styled from '@emotion/styled';
import { Button } from 'react-magma-dom';
import { COLORS, DOTTED_FOCUS, MAIN_CONTENT_SIZE } from '../../constants';
import { VisibilityState } from '../../types';
import { exemplarContentStyles, exemplarCSSProperties, exemplarTitleStyles } from '../../constants/exemplar';

type DefaultScreen = {
  isPresentationMode?: boolean;
};

type Section = {
  hidden: boolean;
  isSmall?: boolean;
  textAlign?: string;
  bgColor?: string;
  highlight?: boolean;
  isAccessible?: boolean;
  isStructureMode?: boolean;
};

interface SectionLabel extends Section {
  wrapLabel: boolean;
}

type Label = {
  highlight?: boolean;
};

type Word = {
  highlighted: boolean;
  color?: string;
  isClickable: boolean;
  styles?: string;
  isHidden?: boolean;
  isUnderlined?: boolean;
  isDottedUnderlined?: boolean;
  isSpace?: boolean;
  isLangFeatures?: boolean;
};

type ControlButton = {
  color: string;
  disabled: boolean;
  margin: string;
  withOpacity: boolean;
  borderRadius: string;
  height: string;
  fontSize: string;
  border?: string;
};

type LabelTitle = {
  color?: string;
};

export const StyledSectionLabel = styled.div<SectionLabel>`
  padding: 9px 0px;
  min-width: 32px;
  font-size: 12px;
  font-weight: 600;
  font-family: Lexend;
  line-height: normal;
  letter-spacing: normal;
  background-color: ${({ bgColor }) => bgColor};
  margin-right: 17px;
  display: ${({ isStructureMode }) => (isStructureMode ? 'flex' : 'none')};
  justify-content: center;
  align-items: center;
  visibility: ${({ highlight, hidden }) => (highlight && !hidden ? 'visible' : 'hidden')};

  div {
    max-width: 100%;
    writing-mode: vertical-rl;
    transform: rotate(180deg);
    white-space: break-spaces;
    max-height: ${({ wrapLabel }) => (wrapLabel ? '80px' : 'none')};
    vertical-align: middle;
    text-align: center;
  }
`;

export const StyledSection = styled.div<Section>`
  ${({ textAlign }) => textAlign && `text-align: ${textAlign};`}
  display: ${({ isStructureMode }) => (isStructureMode ? 'flex' : 'block')};
  ${({ isStructureMode, textAlign }) => isStructureMode && textAlign && 'justify-content: space-between'};
  visibility: ${({ hidden }) => (hidden ? 'hidden' : 'visible')};
  flex-grow: 3;
  background-color: ${({ bgColor, highlight }) => (highlight ? bgColor : 'transparent')};
  ${({ isSmall }) => isSmall && `font-size: 16px`};

  h2 {
    margin: 0;
  }

  ${({ isAccessible, hidden }) =>
    !isAccessible &&
    `
      opacity: ${hidden ? 0 : 0.1};
      pointer-events: none;
  `}
`;

export const StyledExemplarHeading = styled.h2<{
  isAccessible: boolean;
  position?: string;
  color?: string;
}>`
  display: inline-block;
  margin: 32px 32px 24px 26px;
  font-family: 'Mulish';
  font-size: 30px;
  word-spacing: 7px;
  max-width: calc(50% - 70px);

  ${({ position }) => position && `position: ${position};`}

  ${({ color }) => color && `color: ${color};`}

  ${({ isAccessible }) =>
    !isAccessible &&
    `
      opacity: 0.1;
      pointer-events: none;
  `}
`;

export const StyledText = styled.div<{
  textColor?: string;
  isHidden: boolean;
}>`
  ${({ textColor }) => textColor && `color: ${textColor};`}
  letter-spacing: normal;
  visibility: ${({ isHidden }) => (isHidden ? 'hidden' : 'visible')};
  position: relative;
  z-index: 10;
  display: flex;
  flex-direction: column;
  justify-content: center;
  margin: 11px 0;
  max-width: 100%;
  word-spacing: 3.5px;
  h2 {
    margin: 0;
  }
`;

export const StyledScreen = styled.div`
  display: flex;
  flex-wrap: nowrap;
  margin-bottom: auto;
  padding: 32px 0px 20px 0px;
`;

export const StyledDefaultScreen = styled.div<DefaultScreen>`
  display: flex;
  flex: 1;
  justify-content: ${({ isPresentationMode }) => (isPresentationMode ? 'normal' : 'center')};
  padding: ${({ isPresentationMode }) => (isPresentationMode ? '21px 0px 32px 0px' : '21px 0px 0px 0px')};
`;

export const LineBreak = styled.div`
  height: 20px;
`;

export const StyledTitle = styled.span<{ stage: string; color?: string }>`
  ${({ stage }) => exemplarTitleStyles(stage)};
  font-weight: 800;
  color: ${({ color }) => color};
  word-spacing: 2.1px;
`;

export const StyledSubHeader = styled.div<{
  stage: string;
  fontSize: number;
  color: string;
}>`
  font-family: ${({ stage }) => exemplarCSSProperties(stage).content.fontFamily};
  color: ${({ color }) => color};
  margin: 11px 0px 7px 0px;
  font-size: ${({ fontSize }) => fontSize}px;
  line-height: 1.48;
  letter-spacing: 0.46px;
  font-weight: 700;
`;

export const StyledScreenWrapper = styled.div<{ stage?: string }>`
  margin: 0 auto;
  position: relative;
  background-color: ${COLORS.white};
  width: 100%;
  max-width: 926px;
  min-height: ${MAIN_CONTENT_SIZE.M[1]}px;

  letter-spacing: calc(1em / 20);

  ${({ stage }) => exemplarContentStyles(stage)}

  user-select: none;

  z-index: 100;

  h2 {
    font-size: 30px;
    line-height: 36px;
  }
`;

export const StyledLabel = styled.div<Label>`
  overflow-wrap: normal;
  display: flex;
  flex-direction: column;
  font-size: 12px;
  box-sizing: border-box;
  margin-left: 8px;

  cursor: pointer;

  opacity: ${({ highlight }) => (highlight ? '1' : '0.7')};

  &:first-of-type {
    margin-left: 0;
  }
`;

export const StyledSectionLabelsControlWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
`;

export const StyledSectionLabelsControl = styled.div`
  display: flex;
  margin: -8px 20px 20px 0;
  box-sizing: border-box;
  border: solid 1px #dfdfdf;
  padding: 12px 11px 11px 13px;

  font-family: Lexend;
  letter-spacing: normal;
  line-height: normal;
  font-size: 12px;
`;

export const StyledLabelTitle = styled.div<LabelTitle>`
  width: 100%;
  padding: 8px;
  box-sizing: border-box;

  font-weight: 600;
  line-height: 1;

  background-color: ${({ color }) => color};
`;

const getStyledLabelWidth = (hasText: boolean): string | null => {
  const defaultStyle = `
    max-width: 150px;
    min-width: -moz-available;
    min-width: -webkit-fill-available;
  `;

  return hasText ? defaultStyle : null;
};

export const StyledLabelText = styled.div<{ color: string; hasText: boolean }>`
  padding: 8px;
  min-height: 63px;
  ${({ hasText }) => getStyledLabelWidth(hasText)}
  box-sizing: border-box;
  flex-grow: 3;

  font-size: 10px;
  font-weight: normal;
  line-height: normal;

  background-color: ${({ color }) => color};
`;

export const StyledLanguageFeaturesControl = styled.div`
  display: flex;
  padding: 10px 9px 10px 9px;
  margin: 22px 0px 22px auto;
  border: 1px solid #dfdfdf;
  width: fit-content;
`;

export const StyledWordsLine = styled.span<{ margin: string }>`
  margin: ${({ margin }) => margin};
`;

export const StyledSpace = styled.span<{
  isUnderlined: boolean;
  isLanguageFeaturesPage: boolean;
  width?: number;
}>`
  ${({ width }) => width && `display: inline-block; width: ${width}px;`}
  ${({ isUnderlined, isLanguageFeaturesPage }) =>
    isUnderlined && `& > span { border-bottom: solid 2px ${isLanguageFeaturesPage ? COLORS.black : COLORS.black50}; }`}
`;

export const StyledHighlightedWord = styled.span<Word>`
  cursor: ${({ isClickable }) => (isClickable ? 'pointer' : 'default')};
  visibility: ${({ isHidden }) => (isHidden ? 'hidden' : 'visible')};

  ${({ isLangFeatures, isSpace, highlighted, color }) =>
    isSpace
      ? `background-color: ${highlighted ? color : ''};`
      : isLangFeatures
        ? `
            &>span>span {
              background-color: ${highlighted ? color : ''};
            }
          `
        : `
            span {
              background-color: ${highlighted ? color : ''};
            }
          `}

  ${({ isDottedUnderlined }) => isDottedUnderlined && `& > span { border-bottom: dotted 2px ${COLORS.black}; }`}
  ${({ isUnderlined, isLangFeatures }) =>
    isUnderlined && `{ border-bottom: solid 2px ${isLangFeatures ? COLORS.black : COLORS.black50}; }`}
`;

export const SpacedSpan = styled.span<{
  padding?: string;
  margin?: string;
  color?: string;
  wordStyles: string;
  isHidden?: boolean;
  isDottedUnderlined?: boolean;
}>`
  ${({ padding }) => padding && `padding: ${padding};`};
  ${({ margin }) => margin && `margin: ${margin}`};
  color: ${({ color }) => (color ? color : '')};
  ${({ wordStyles }) => wordStyles};
  visibility: ${({ isHidden }) => (isHidden ? 'hidden' : 'visible')};
  ${({ isDottedUnderlined }) => isDottedUnderlined && `border-bottom: dotted 2px ${COLORS.black};`}
`;

export const StyledControlButton = styled(({ ...rest }) => (
  <Button {...rest} aria-label="LF CONTROL BUTTON" />
))<ControlButton>`
  font-family: Lexend !important;
  font-size: ${({ fontSize }) => (fontSize ? fontSize : '14px')}!important;
  min-width: 0px !important;
  margin: ${({ margin }) => margin} !important;
  border-radius: ${({ borderRadius }) => (borderRadius ? borderRadius : '0px !important')};
  text-transform: none !important;
  height: ${({ height }) => (height ? height : '30px')}!important;
  padding: 6px 16px 6px 16px !important;
  opacity: ${({ withOpacity }) => (withOpacity ? 0.5 : 1)}!important;
  background-color: ${({ color }) => color}!important;
  disabled: ${({ disabled }) => (disabled ? 'disabled' : '')}!important;
  color: black !important;

  ${({ border, height }) =>
    border &&
    `
    &>span {
      border-bottom: ${border} 2px ${COLORS.black};
      height: ${height ? height : '30px'};
    }
  `}

  :focus {
    color: black !important;
    background: ${({ color }) => color} !important;
  }
  :hover {
    color: black !important;
    background: ${({ color }) => color} !important;
  }
`;

export const StyledParentControlWrapper = styled.div`
  padding: 8px 6px 8px;
  button {
    text-transform: initial;
    height: 24px;
    border-radius: 40px;
  }
`;

export const SideWrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 3;
  max-width: 50%;
  height: fit-content;
  overflow-wrap: anywhere;
  :first-of-type {
    padding-bottom: 12px;
  }
`;

export const StyledSideWrapper = styled.div<{
  isImageStickedToBottom?: boolean;
  isExtending?: boolean;
}>`
  width: 50%;
  z-index: 2;
  overflow-wrap: anywhere;
  word-spacing: 3.5px;

  ${({ isImageStickedToBottom }) =>
    isImageStickedToBottom &&
    `
    display: flex;
    flex-direction: column;
    justify-content: space-between;
  `}

  :first-of-type {
    margin-right: ${({ isExtending }) => (isExtending ? '28px' : '32px')};
  }
`;

interface IContainer {
  visibility: VisibilityState;
  isAccessible: boolean;
  color?: string;
}

export const StyledContainerWrapper = styled.div<IContainer>`
  visibility: ${({ visibility }) => visibility};
`;

export const StyledContainer = styled.div<IContainer>`
  ${DOTTED_FOCUS}
  ${({ visibility }) =>
    visibility === 'hidden'
      ? `
        border: 1px dashed #3f3f3f;
        background-color: #f2f2f2;
      `
      : `
        border: 1px dashed transparent;
      `}

  ${({ isAccessible }) =>
    !isAccessible &&
    `
      opacity: 0.1;
      pointer-events: none;
  `}

  ${({ color }) => color && `color: ${color};`}
  margin: 22px 0px 0px 0px;
  font-size: 23px;
  line-height: 1.45;
  letter-spacing: 0.46px;
  text-align: left;
  z-index: 10;

  &:first-of-type {
    margin-top: 8px;
  }
`;

export const StyledSentence = styled.div`
  ${DOTTED_FOCUS}
  margin: 16px 0px 0px 0px;
`;

export const StyledExemplarWrapper = styled.div<{ padding?: string }>`
  padding: ${({ padding }) => padding};
  display: flex;
  flex-direction: column;
  min-height: ${MAIN_CONTENT_SIZE.M[1]}px;
`;

export const StyledEmergentContainer = styled.div`
  position: relative;
  min-height: ${MAIN_CONTENT_SIZE.M[1]}px;
`;
